import { c as _c } from "react/compiler-runtime";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { ArrowUp, Plus } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";
import { AppHeaderBar, PanelTogglePlaceholder } from "@/components/ui/app-header";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { peekSkeletonComposerText, requestSkeletonComposerSubmit, saveSkeletonComposerText } from "./newSessionSkeletonState";
import { NEW_SESSION_MASCOT_SIZE_CLASS, NEW_SESSION_SCROLL_AREA_CLASS, NEW_SESSION_WELCOME_COLUMN_CLASS } from "./newSessionLayout";
import { mascotHolidayAnywhere, mascotHolidayOn, newSessionMascotPath } from "./holidayMascot";
import { PROMPT_PILL_ROW_CLASS, PromptPillSkeleton, LEAD_STARTER_PILL_LAYOUT_CLASS, STARTER_PILL_ROW_CLASS, StarterPromptPillSkeleton } from "./promptPills";
import { SidebarSkeleton } from "./SidebarSkeleton";
import { readLeftSidebarSkeletonLayout, useIsHydrated } from "./skeletonLayout";
import { useIsTouchPrimary } from "@/hooks/use-device";
import { assetUrl } from "@/lib/assetUrl";
import { useCodesmithSurface } from "@/lib/codesmithSurface";
import { splitInstanceBasePath } from "@/lib/dashboard-routes";
import { cn } from "@repo/blacksmith-ui/cn";
import { CENTERED_LOADING_ANVIL_SIZE_CLASS, CENTERED_LOADING_ANVIL_SIZE_PX } from "@/components/agent-session/centeredLoadingAnvil";
import { useAnvilHandoffSource } from "@/components/agent-session/useAnvilHandoff";
import { animationPath } from "@/components/agent-session/useAnvilTransition";

/* ---------------------------------------------------------------------------
 * Structural skeletons for the Codesmith session view.
 *
 * SessionLoadingScreen    – centered animated anvil for existing sessions.
 *                           Used inside Codesmith.tsx where the real sidebar
 *                           is already rendered alongside.
 *
 * NewSessionChatSkeleton  – the new-session welcome layout (centered mascot,
 *                           greeting, composer, starter rows).
 *
 * SessionSkeleton         – sidebar + main pane. Used in the page-level
 *                           Suspense fallback where the entire Codesmith view
 *                           is replaced; picks the main-pane variant from the
 *                           current route.
 * -------------------------------------------------------------------------*/

export const SESSION_LOADING_LOOP = "ruminating";

/** Loading state for an existing session. */
export function SessionLoadingScreen() {
  const $ = _c(1);
  const spriteRef = useRef(null);
  useAnvilHandoffSource(spriteRef, SESSION_LOADING_LOOP);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div role="status" aria-label="Loading session" className="flex min-w-0 flex-1 items-center justify-center bg-[var(--color-app-background)]"><span ref={spriteRef} aria-hidden={true} className={cn(CENTERED_LOADING_ANVIL_SIZE_CLASS, "flex items-center justify-center")}><img src={animationPath(SESSION_LOADING_LOOP, "light")} alt="" width={CENTERED_LOADING_ANVIL_SIZE_PX} height={CENTERED_LOADING_ANVIL_SIZE_PX} draggable={false} className="select-none object-contain [image-rendering:pixelated] dark:hidden" /><img src={animationPath(SESSION_LOADING_LOOP, "dark")} alt="" width={CENTERED_LOADING_ANVIL_SIZE_PX} height={CENTERED_LOADING_ANVIL_SIZE_PX} draggable={false} className="hidden select-none object-contain [image-rendering:pixelated] dark:block" /></span></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * `NewSessionView` mounts PrActionCards only when `pr_preview` hydrates a
 * `previewManagedPr` (see Codesmith.tsx) — the "View in Codesmith" deep link
 * carries the PR metadata as JSON in that param. A bare `pr` param (a
 * resolve-pr landing with no metadata, or a `repo`+`pr` Fix-with-Codesmith
 * link) resolves into TaskStarterCards instead, so only `pr_preview` reserves
 * the PrActionCards skeleton shape (four action pills).
 */
function hasPrPreviewInSearch(search: string): boolean {
  return new URLSearchParams(search).has("pr_preview");
}

/** Placeholder pill count matching PrActionCards' four action pills. */
const PR_ACTION_PILL_COUNT = 4;

/**
 * Mirrors NewSessionView's non-compact layout so the fallback resolves into
 * the real page without reshaping. The mascot and composer are static chrome
 * rendered for real; the user-dependent greeting and starter rows shimmer.
 * The composer is a live textarea whose text hands off
 * to the real composer's draft (see newSessionSkeletonState), so the user
 * can start typing before the app finishes loading. Enter follows the real
 * composer's keymap (send on desktop, newline on touch) and the send button
 * works like Enter; the send itself waits for the real composer.
 */
export function NewSessionChatSkeleton(t0) {
  const $ = _c(47);
  const {
    search
  } = t0;
  const isHydrated = useIsHydrated();
  const isTouch = useIsTouchPrimary();
  let t1;
  if ($[0] !== isHydrated || $[1] !== search) {
    t1 = isHydrated && hasPrPreviewInSearch(search ?? window.location.search);
    $[0] = isHydrated;
    $[1] = search;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isPrAttached = t1;
  const seededText = isHydrated ? peekSkeletonComposerText() ?? "" : "";
  const mascotHoliday = isHydrated ? mascotHolidayOn(new Date()) : null;
  let t2;
  if ($[3] !== isHydrated) {
    t2 = !isHydrated && mascotHolidayAnywhere(new Date());
    $[3] = isHydrated;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const mascotDeferred = t2;
  const mascotSrc = theme => mascotDeferred ? undefined : assetUrl(newSessionMascotPath("hello", theme, mascotHoliday));
  const mascotVisibility = mascotDeferred ? "invisible" : "";
  const textareaRef = useRef(null);
  const [typedHasText, setTypedHasText] = useState(null);
  const hasText = typedHasText ?? seededText.trim().length > 0;
  const requestSubmit = _temp;
  let t3;
  if ($[5] !== isHydrated) {
    t3 = isHydrated && !readLeftSidebarSkeletonLayout().visible && <PanelTogglePlaceholder />;
    $[5] = isHydrated;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <AppHeaderBar inset="canvas" className="bg-app-background">{t3}</AppHeaderBar>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = mascotSrc("dark");
  const t6 = `hidden ${NEW_SESSION_MASCOT_SIZE_CLASS} dark:block ${mascotVisibility}`;
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <img src={t5} alt="[code]smith" className={t6} />;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex h-[25px] items-center"><Skeleton className="h-5 w-72 max-w-full rounded" /></div>;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== isHydrated || $[14] !== seededText) {
    t9 = el => {
      textareaRef.current = el;
      if (!el) {
        return;
      }
      const handoff = peekSkeletonComposerText() ?? "";
      if (isHydrated && handoff !== seededText) {
        el.value = handoff;
      }
      el.setSelectionRange(el.value.length, el.value.length);
    };
    $[13] = isHydrated;
    $[14] = seededText;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== setTypedHasText) {
    t10 = e => {
      saveSkeletonComposerText(e.target.value);
      setTypedHasText(e.target.value.trim().length > 0);
    };
    $[16] = setTypedHasText;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== isTouch || $[19] !== requestSubmit) {
    t11 = e_0 => {
      if (e_0.nativeEvent.isComposing || e_0.keyCode === 229) {
        return;
      }
      if (e_0.key !== "Enter" || e_0.shiftKey || isTouch) {
        return;
      }
      e_0.preventDefault();
      requestSubmit(e_0.currentTarget.value);
    };
    $[18] = isTouch;
    $[19] = requestSubmit;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== seededText || $[22] !== t10 || $[23] !== t11 || $[24] !== t9) {
    t12 = <textarea ref={t9} rows={1} autoFocus={true} autoComplete="off" data-1p-ignore={true} spellCheck={false} aria-label="Chat input" placeholder="Got a task or question?" defaultValue={seededText} onChange={t10} onKeyDown={t11} className="type-chat-input pointer-events-auto w-full resize-none overflow-hidden border-0 bg-transparent p-0 pl-[31px] md:pl-[29px] pr-9 text-neutral-900 placeholder-neutral-500 outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 dark:text-neutral-100 dark:placeholder-neutral-500/70" />;
    $[21] = seededText;
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="absolute inset-y-0 left-0 flex items-center gap-1"><span className="-ml-2 md:-ml-[10px] flex size-8 items-center justify-center rounded-full text-icon-secondary"><Plus className="size-[18px]" strokeWidth={2} aria-hidden={true} /></span></div>;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const t14 = !hasText;
  let t15;
  if ($[27] !== requestSubmit) {
    t15 = () => requestSubmit(textareaRef.current?.value ?? "");
    $[27] = requestSubmit;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const t16 = hasText ? "cursor-pointer border-[#2a2c2c] bg-blacksmith-yellow text-neutral-900 hover:brightness-95 dark:text-neutral-900" : "cursor-not-allowed border-black/[0.08] bg-[var(--brand-gradient-yellow-tertiary)] text-neutral-400 dark:text-neutral-500";
  let t17;
  if ($[29] !== t16) {
    t17 = cn("pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full border transition-all focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:outline-none focus-visible:ring-0", t16);
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ArrowUp className="size-3.5" strokeWidth={2.5} aria-hidden={true} />;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t14 || $[33] !== t15 || $[34] !== t17) {
    t19 = <div className="absolute inset-y-0 right-0 flex items-center"><button type="button" aria-label="Send message" disabled={t14} onClick={t15} className={t17}>{t18}</button></div>;
    $[32] = t14;
    $[33] = t15;
    $[34] = t17;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t12 || $[37] !== t19) {
    t20 = <div className="rounded-[32px] border border-[var(--color-border)] bg-white pl-3 pr-1.5 py-1 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.08)] md:pl-4 md:pr-2 md:py-1.5 dark:bg-neutral-900 dark:shadow-[0px_10px_20px_0px_rgba(0,0,0,0.3)]"><div className="relative flex min-h-8 items-center">{t12}{t13}{t19}</div></div>;
    $[36] = t12;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  const t21 = isPrAttached ? PROMPT_PILL_ROW_CLASS : STARTER_PILL_ROW_CLASS;
  let t22;
  if ($[39] !== isPrAttached) {
    t22 = isPrAttached ? Array.from({
      length: PR_ACTION_PILL_COUNT
    }, _temp2) : <><StarterPromptPillSkeleton className={LEAD_STARTER_PILL_LAYOUT_CLASS} /><StarterPromptPillSkeleton className="grow" /></>;
    $[39] = isPrAttached;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t21 || $[42] !== t22) {
    t23 = <div className="flex w-full flex-col gap-4"><div className={t21}>{t22}</div></div>;
    $[41] = t21;
    $[42] = t22;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== t20 || $[45] !== t23) {
    t24 = <div className="flex w-full flex-col gap-3">{t20}{t23}</div>;
    $[44] = t20;
    $[45] = t23;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  return <div className="pointer-events-none flex min-w-0 flex-1 flex-col bg-[var(--color-app-background)]">{t4}<div className={NEW_SESSION_SCROLL_AREA_CLASS}><div className={`relative mb-auto flex max-w-full flex-col px-4 ${NEW_SESSION_WELCOME_COLUMN_CLASS}`}><div className="flex flex-col items-center"><div className="relative mt-1"><img src={mascotSrc("light")} alt="[code]smith" className={`${NEW_SESSION_MASCOT_SIZE_CLASS} dark:hidden ${mascotVisibility}`} />{t7}</div>{t8}</div>{t24}</div></div></div>;
}

/**
 * `/[org]/sessions` and `/[org]/sessions/new` land on NewSessionView, so the
 * fallback should be welcome-shaped there rather than chat-transcript-shaped.
 * An enterprise instance prefix (`/enterprise/ghe/acme/sessions/new`) is
 * stripped first.
 */
function _temp2(_, i) {
  return <PromptPillSkeleton key={i} className="w-32" />;
}
function _temp(text) {
  if (text.trim()) {
    requestSkeletonComposerSubmit(text);
  }
}
function isNewSessionPath(path: string): boolean {
  const {
    pathname
  } = splitInstanceBasePath(path.split(/[?#]/)[0]);
  const segments = pathname.split("/").filter(Boolean);
  return segments[1] === "sessions" && (segments.length === 2 || segments[2] === "new");
}

/** Full skeleton (sidebar + main pane) for the page-level Suspense fallback. */
export function SessionSkeleton(t0) {
  const $ = _c(14);
  const {
    sidebar
  } = t0;
  const router = useRouter();
  const surface = useCodesmithSurface(router.query.embed);
  const isHydrated = useIsHydrated();
  let t1;
  if ($[0] !== isHydrated || $[1] !== router.asPath) {
    t1 = isHydrated && router.asPath.includes("/[") ? window.location.pathname + window.location.search : router.asPath;
    $[0] = isHydrated;
    $[1] = router.asPath;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const path = t1;
  let t2;
  if ($[3] !== path) {
    t2 = path.split("#")[0].match(/\?.*/)?.[0] ?? "";
    $[3] = path;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const search = t2;
  let t3;
  if ($[5] !== sidebar || $[6] !== surface) {
    t3 = sidebar ? <div className="pointer-events-auto contents">{sidebar}</div> : <SidebarSkeleton showSearch={surface !== "extension"} />;
    $[5] = sidebar;
    $[6] = surface;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== path || $[9] !== search) {
    t4 = isNewSessionPath(path) ? <NewSessionChatSkeleton search={search} /> : <SessionLoadingScreen />;
    $[8] = path;
    $[9] = search;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="pointer-events-none flex h-full">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}